/**
 * Knowledge Base hooks for admin functionality
 *
 * These hooks provide TanStack Query integration for KB management,
 * including CRUD operations, filtering, and statistics.
 */

import { useQuery, useMutation, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import {
  listKbEntries,
  getKbEntry,
  createKbEntry,
  updateKbEntry,
  deleteKbEntry,
  markEntryStale,
  clearEntryStale,
  checkDuplicates,
  getKbCategories,
  getKbStats,
  refreshEmbeddings,
  type KbEntryFilters,
  type CreateEntryRequest,
  type UpdateEntryRequest,
  type KbEntry,
  type KbEntryListResponse,
  type KbStats,
  type DuplicateCheckResult,
  type RefreshEmbeddingsResult,
} from '@/api/kb';
import {
  getResolvedStory,
  getResolvedNodeAnswers,
  type ResolvedCategory,
  editSnippet,
  setHeaderNotApplicable,
  promoteRegionHeader,
  createContentBlock,
  placeScope,
  listContentBlocks,
  setContentBlockStatus,
  deleteScope,
  type ContentStatus,
  type RegionPromoted,
  type ResolvedStoryResponse,
  type SnippetUpdated,
  type ContentBlockCreated,
  type CreateContentBlockInput,
  type ScopePlacementCreated,
  type PlaceScopeInput,
  type ContentBlockList,
} from '@/api/kbResolvedStory';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const kbQueryKeys = {
  all: ['kb'] as const,
  entries: () => [...kbQueryKeys.all, 'entries'] as const,
  entriesList: (filters?: KbEntryFilters) => [...kbQueryKeys.entries(), 'list', filters] as const,
  entry: (id: number) => [...kbQueryKeys.entries(), id] as const,
  stats: (firmScope?: number | null) => [...kbQueryKeys.all, 'stats', firmScope ?? null] as const,
  categories: () => [...kbQueryKeys.all, 'categories'] as const,
  duplicates: (questionText: string) => [...kbQueryKeys.all, 'duplicates', questionText] as const,
  resolvedStory: (rootNodeId?: number) =>
    ['kb', 'resolvedStory', rootNodeId ?? null] as const,
};

// -----------------------------------------------------------------------------
// Query Hooks
// -----------------------------------------------------------------------------

export interface UseKbEntriesOptions {
  enabled?: boolean;
}

/**
 * Hook to list knowledge base entries with filters
 */
export function useKbEntries(filters?: KbEntryFilters, options?: UseKbEntriesOptions) {
  return useQuery<KbEntryListResponse, Error>({
    queryKey: kbQueryKeys.entriesList(filters),
    queryFn: () => listKbEntries(filters),
    staleTime: 30_000, // Consider fresh for 30 seconds
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to get a single knowledge base entry
 */
export function useKbEntry(entryId: number | null, options?: { enabled?: boolean }) {
  return useQuery<KbEntry, Error>({
    queryKey: kbQueryKeys.entry(entryId ?? 0),
    queryFn: () => getKbEntry(entryId!),
    enabled: !!entryId && (options?.enabled ?? true),
    staleTime: 60_000, // Consider fresh for 1 minute
  });
}

/**
 * Hook to get knowledge base statistics
 */
export function useKbStats(options?: { enabled?: boolean }): UseQueryResult<KbStats, Error>;
export function useKbStats(
  firmId?: number | null,
  options?: { enabled?: boolean }
): UseQueryResult<KbStats, Error>;
export function useKbStats(
  firmIdOrOptions?: number | null | { enabled?: boolean },
  options?: { enabled?: boolean }
) {
  const firmId = typeof firmIdOrOptions === 'object' ? undefined : firmIdOrOptions;
  const queryOptions = typeof firmIdOrOptions === 'object' ? firmIdOrOptions : options;
  return useQuery<KbStats, Error>({
    queryKey: kbQueryKeys.stats(firmId ?? null),
    queryFn: () => getKbStats(firmId ?? undefined),
    staleTime: 60_000, // Consider fresh for 1 minute
    enabled: queryOptions?.enabled ?? true,
  });
}

/**
 * Hook to get the resolved product-story tree with inheritance provenance
 * (Wellington #5, read-only). Pass `rootNodeId` to resolve a subtree; omit to
 * use the firm's product root.
 */
export function useResolvedStory(opts?: {
  rootNodeId?: number;
  enabled?: boolean;
}) {
  return useQuery<ResolvedStoryResponse, Error>({
    queryKey: kbQueryKeys.resolvedStory(opts?.rootNodeId),
    queryFn: () => getResolvedStory(opts?.rootNodeId),
    staleTime: 300_000,
    enabled: opts?.enabled ?? true,
  });
}

/**
 * Lazily load the exact resolved answers for one (node, category) — the bounded
 * detail read behind the master-detail resolved-story view. Enabled only when a
 * node + category are selected, so the skeleton tree stays cheap.
 */
export function useResolvedNodeAnswers(opts: {
  nodeId: number | null;
  categoryNodeId: number | null;
  enabled?: boolean;
}) {
  return useQuery<ResolvedCategory, Error>({
    queryKey: ['kb', 'resolvedNode', opts.nodeId, opts.categoryNodeId],
    queryFn: () => getResolvedNodeAnswers(opts.nodeId as number, opts.categoryNodeId),
    staleTime: 300_000,
    enabled: (opts.enabled ?? true) && opts.nodeId !== null,
  });
}

/**
 * Hook to get available categories
 */
export function useKbCategories(options?: { enabled?: boolean }) {
  return useQuery<string[], Error>({
    queryKey: kbQueryKeys.categories(),
    queryFn: () => getKbCategories(),
    staleTime: 300_000, // Consider fresh for 5 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to check for duplicates
 */
export function useCheckDuplicates(questionText: string, options?: { enabled?: boolean }) {
  return useQuery<DuplicateCheckResult, Error>({
    queryKey: kbQueryKeys.duplicates(questionText),
    queryFn: () => checkDuplicates(questionText),
    enabled: !!questionText && questionText.length > 10 && (options?.enabled ?? true),
    staleTime: 60_000,
  });
}

// -----------------------------------------------------------------------------
// Mutation Hooks
// -----------------------------------------------------------------------------

/**
 * Hook to create a new knowledge base entry
 */
export function useCreateKbEntry() {
  const queryClient = useQueryClient();

  return useMutation<KbEntry, Error, CreateEntryRequest>({
    mutationFn: (request) => createKbEntry(request),
    onSuccess: () => {
      // Invalidate entries list and stats
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
      queryClient.invalidateQueries({ queryKey: [...kbQueryKeys.all, 'stats'] });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.categories() });
    },
  });
}

/**
 * Hook to update a knowledge base entry
 */
export function useUpdateKbEntry() {
  const queryClient = useQueryClient();

  return useMutation<KbEntry, Error, { entryId: number; request: UpdateEntryRequest }>({
    mutationFn: ({ entryId, request }) => updateKbEntry(entryId, request),
    onSuccess: (data, { entryId }) => {
      // Update the specific entry in cache
      queryClient.setQueryData(kbQueryKeys.entry(entryId), data);
      // Invalidate entries list
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.categories() });
    },
  });
}

/**
 * Hook to delete a knowledge base entry
 */
export function useDeleteKbEntry() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (entryId) => deleteKbEntry(entryId),
    onSuccess: (_, entryId) => {
      queryClient.removeQueries({ queryKey: kbQueryKeys.entry(entryId) });
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
      queryClient.invalidateQueries({ queryKey: [...kbQueryKeys.all, 'stats'] });
    },
  });
}

/**
 * Hook to mark an entry as stale
 */
export function useMarkStale() {
  const queryClient = useQueryClient();

  return useMutation<KbEntry, Error, { entryId: number; reason: string }>({
    mutationFn: ({ entryId, reason }) => markEntryStale(entryId, reason),
    onSuccess: (entry) => {
      queryClient.setQueryData(kbQueryKeys.entry(entry.id), entry);
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
      queryClient.invalidateQueries({ queryKey: [...kbQueryKeys.all, 'stats'] });
    },
  });
}

/**
 * Hook to clear stale flag from an entry
 */
export function useClearStale() {
  const queryClient = useQueryClient();

  return useMutation<KbEntry, Error, number>({
    mutationFn: (entryId) => clearEntryStale(entryId),
    onSuccess: (entry) => {
      queryClient.setQueryData(kbQueryKeys.entry(entry.id), entry);
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
      queryClient.invalidateQueries({ queryKey: [...kbQueryKeys.all, 'stats'] });
    },
  });
}

/**
 * Hook to refresh embeddings
 */
export function useRefreshEmbeddings() {
  const queryClient = useQueryClient();

  return useMutation<RefreshEmbeddingsResult, Error, number[] | undefined>({
    mutationFn: (entryIds) => refreshEmbeddings(entryIds),
    onSuccess: () => {
      // Invalidate all entries to show updated state
      queryClient.invalidateQueries({ queryKey: kbQueryKeys.entries() });
    },
  });
}

/**
 * Hook to edit a snippet's body (copy-on-write). On success it invalidates every
 * resolved-story query, so the new text re-resolves at every node that inherits
 * the snippet — the "edit once, applies everywhere" propagation, made visible.
 */
export function useEditSnippet() {
  const queryClient = useQueryClient();

  return useMutation<
    SnippetUpdated,
    Error,
    { blockKey: string; bodyText: string; expectedVersion?: number; bodyHtml?: string }
  >({
    mutationFn: ({ blockKey, bodyText, expectedVersion, bodyHtml }) =>
      editSnippet(blockKey, bodyText, expectedVersion, bodyHtml),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] });
    },
  });
}

/** Mark a canonical level-header not-applicable (flat-firm opt-out), or clear it. */
export function useSetHeaderNotApplicable() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { nodeId: number; notApplicable: boolean }>({
    mutationFn: ({ nodeId, notApplicable }) => setHeaderNotApplicable(nodeId, notApplicable),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] }),
  });
}

/** Lazy-promote a Region header into authorable region instances. */
export function usePromoteRegionHeader() {
  const queryClient = useQueryClient();
  return useMutation<RegionPromoted, Error, number>({
    mutationFn: (nodeId) => promoteRegionHeader(nodeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] }),
  });
}

/** The firm's content-block library (for picking a block to place). */
export function useContentBlocks(enabled = true) {
  return useQuery<ContentBlockList>({
    queryKey: ['kb', 'contentBlocks'],
    queryFn: () => listContentBlocks(),
    enabled,
  });
}

/** Create a content block (Wellington #2 authoring). */
export function useCreateContentBlock() {
  const queryClient = useQueryClient();
  return useMutation<ContentBlockCreated, Error, CreateContentBlockInput>({
    mutationFn: (input) => createContentBlock(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'contentBlocks'] });
    },
  });
}

/** Transition a block's status (e.g. draft → approved before placing). */
export function useSetContentBlockStatus() {
  const queryClient = useQueryClient();
  return useMutation<ContentBlockCreated, Error, { blockKey: string; status: ContentStatus }>({
    mutationFn: ({ blockKey, status }) => setContentBlockStatus(blockKey, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kb', 'contentBlocks'] });
      queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] });
    },
  });
}

/** Place a block on a product-spine node so it inherits to descendants. */
export function usePlaceScope() {
  const queryClient = useQueryClient();
  return useMutation<ScopePlacementCreated, Error, PlaceScopeInput>({
    mutationFn: (input) => placeScope(input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] }),
  });
}

/** Un-place a snippet (soft-delete its placement). */
export function useDeleteScope() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (scopeId) => deleteScope(scopeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['kb', 'resolvedStory'] }),
  });
}

// -----------------------------------------------------------------------------
// Re-exports
// -----------------------------------------------------------------------------

export type {
  KbEntry,
  KbEntryListResponse,
  KbEntryFilters,
  KbStats,
  CreateEntryRequest,
  UpdateEntryRequest,
  DuplicateCheckResult,
  KbSourceType,
} from '@/api/kb';

export type {
  ResolvedStoryResponse,
  ResolvedNode,
  ResolvedCategory,
  ResolvedQuestionOut,
  ResolvedSnippetOut,
  ResolvedStatus,
} from '@/api/kbResolvedStory';
